'use client'

import { type ReactNode, useReducer, useRef, useState, useEffect, Children } from 'react'

import { createPortal } from 'react-dom'

import { tw } from '@/lib/utils'

import ModelFilterSelect from './model-filter-select'

interface ModelInfo {
  id: number
  name: string
  panelKey: string
}

interface ProductAccessoriesClientProps {
  models: ModelInfo[]
  allLabel: string
  compatibleLabel: string
  showMoreLabel: string
  showLessLabel: string
  panelCounts: Record<string, number>
  accessoriesLimit: number
  children: ReactNode
}

interface State {
  activePanelId: string
  expandedPanels: Set<string>
}

type Action = { type: 'SELECT_PANEL'; panelId: string } | { type: 'TOGGLE_EXPAND'; panelId: string }

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SELECT_PANEL':
      return { ...state, activePanelId: action.panelId }
    case 'TOGGLE_EXPAND': {
      const next = new Set(state.expandedPanels)
      if (next.has(action.panelId)) {
        next.delete(action.panelId)
      } else {
        next.add(action.panelId)
      }
      return { ...state, expandedPanels: next }
    }
    default:
      return state
  }
}

interface TagTooltipData {
  tag: string
  description: string
}

const AccessoryTagTooltipProvider = ({ children }: { children: ReactNode }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [tooltip, setTooltip] = useState<{ data: TagTooltipData; top: number; left: number } | null>(null)

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest('[data-accessory-tag-tooltip]') as HTMLElement | null
      if (target) {
        e.preventDefault()
        try {
          const data = JSON.parse(target.getAttribute('data-accessory-tag-tooltip')!) as TagTooltipData
          const rect = target.getBoundingClientRect()
          setTooltip({
            data,
            top: rect.top + window.scrollY - 8,
            left: rect.left + window.scrollX + rect.width / 2,
          })
        } catch {
          // ignore parse errors
        }
        return
      }
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setTooltip(null)
      }
    }

    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  return (
    <div ref={containerRef}>
      {children}
      {tooltip &&
        createPortal(
          <div
            ref={popoverRef}
            className="pointer-events-auto absolute z-[9999]"
            style={{ top: tooltip.top, left: tooltip.left, transform: 'translate(-50%, -100%)' }}
          >
            <div className="min-w-[200px] max-w-[400px] rounded-lg bg-white px-3 py-2 shadow-lg ring-1 ring-slate-200">
              <p className="text-navy text-xs font-bold">{tooltip.data.tag}</p>
              <p className="text-navy/80 mt-1 text-sm">{tooltip.data.description}</p>
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}

export const ProductAccessoriesClient = ({
  models,
  allLabel,
  compatibleLabel,
  showMoreLabel,
  showLessLabel,
  panelCounts,
  accessoriesLimit,
  children,
}: ProductAccessoriesClientProps) => {
  const [state, dispatch] = useReducer(reducer, {
    activePanelId: 'all',
    expandedPanels: new Set<string>(),
  })

  const isExpanded = state.expandedPanels.has(state.activePanelId)
  const activePanelKey =
    state.activePanelId === 'all' ? 'all' : models.find((m) => String(m.id) === state.activePanelId)?.panelKey
  const activeAccessoriesCount = activePanelKey ? (panelCounts[activePanelKey] ?? 0) : (panelCounts.all ?? 0)
  const childArray = Children.toArray(children)

  // First child is the description, rest are panels
  const description = childArray[0]
  const panels = childArray.slice(1)

  return (
    <div className="flex max-w-[1344px] flex-col gap-9 pb-6 lg:gap-12 lg:pb-16">
      <div className="flex flex-col justify-between lg:flex-row lg:gap-48">
        {description}
        {models.length > 0 && (
          <div className="flex max-w-[575px] items-center gap-4 pt-6 lg:pt-0">
            <h2 className="hidden text-base -tracking-[0.54px] whitespace-nowrap lg:block">{compatibleLabel}</h2>
            <ModelFilterSelect
              value={state.activePanelId}
              onValueChange={(panelId) => dispatch({ type: 'SELECT_PANEL', panelId })}
              ariaLabel={compatibleLabel}
              options={[
                { value: 'all', label: allLabel },
                ...models.map((model) => ({ value: String(model.id), label: model.name })),
              ]}
            />
          </div>
        )}
      </div>

      <AccessoryTagTooltipProvider>
        <section>
          <ActivePanelRevealer activePanelKey={activePanelKey ?? 'all'} isExpanded={isExpanded}>
            {panels}
          </ActivePanelRevealer>

          {activeAccessoriesCount > accessoriesLimit && (
            <button
              data-tracking="ignore"
              onClick={() => dispatch({ type: 'TOGGLE_EXPAND', panelId: state.activePanelId })}
              type="button"
              aria-expanded={isExpanded}
              data-ht-disclosure-trigger="read-more"
              className="mt-12 flex w-full cursor-pointer items-center justify-center gap-[16px]"
            >
              <span className="text-navy text-xl font-light">{isExpanded ? showLessLabel : showMoreLabel}</span>
              <div className="bg-navy flex items-center justify-center rounded-full p-[15px]">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  focusable="false"
                  className={tw('transition-transform', isExpanded ? '-rotate-90' : 'rotate-90')}
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          )}
        </section>
      </AccessoryTagTooltipProvider>
    </div>
  )
}

const ActivePanelRevealer = ({
  activePanelKey,
  isExpanded,
  children,
}: {
  activePanelKey: string
  isExpanded: boolean
  children: ReactNode
}) => {
  const activeRule = `[data-accessory-card][data-accessory-panels~="${activePanelKey}"]`
  const overflowRule = `[data-accessory-card][data-accessory-overflow-panels~="${activePanelKey}"]`

  // Replay a brief swap animation whenever the active model changes, so a switch
  // is visible even when two models expose the exact same accessory set (very
  // common — most models in a family share accessories). Bumping the key remounts
  // the grid, which restarts the CSS animation; we skip the initial mount so the
  // section doesn't animate on page load.
  const swapCountRef = useRef(0)
  const prevPanelKeyRef = useRef(activePanelKey)
  if (prevPanelKeyRef.current !== activePanelKey) {
    prevPanelKeyRef.current = activePanelKey
    swapCountRef.current += 1
  }
  const hasSwapped = swapCountRef.current > 0

  return (
    <div className={tw('[&_[data-accessory-card]]:hidden')}>
      <style
        dangerouslySetInnerHTML={{
          __html: `
            ${activeRule} {
              display: block !important;
              order: var(--accessory-order-${activePanelKey}, 9999);
            }
            ${
              isExpanded
                ? ''
                : `${overflowRule} {
                    display: none !important;
                  }`
            }
          `,
        }}
      />
      <div key={swapCountRef.current} className={tw(hasSwapped && 'motion-safe:animate-accessorySwap')}>
        {children}
      </div>
    </div>
  )
}
